import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import React, { useCallback, useEffect } from 'react';
import {
  ColumnInstance,
  Row,
  UseColumnOrderState,
  UseResizeColumnsState,
} from 'react-table';

import { useRemToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { CardConfig } from '@work4all/models/lib/table-schema/card-config';

import { IBasicTableProps } from '../../BasicTable';
import { ICssClasses, TableMode, TableRow } from '../../types';
import { SEPARATOR_ROW_HEIGHT } from '../row-render/components/SeparatorRow/SeparatorRow';
import { RenderRow } from '../row-render/RenderRow';

export interface ILoaderProps {
  tableContainerRef: React.RefObject<HTMLDivElement>;
  cardsView: boolean;
  cardConfig: CardConfig | null;
  // selectedRowIds: UseRowSelectState<never>['selectedRowIds'];
  columnResizing: UseResizeColumnsState<never>['columnResizing'];
  columnOrder: UseColumnOrderState<never>['columnOrder'];
  prepareRow: (row: Row) => void;
  rows: TableRow[];
  allItemsCount?: IBasicTableProps['allItemsCount'];
  loadMoreItems?: (
    startIndex: number,
    stopIndex: number,
    rows: TableRow[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any> | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isItemLoaded?: (index: number, row: Row<any>) => boolean;
  onRowDoubleClick?: (id: string) => void;
  onRowClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: TableRow
  ) => void;
  onRowContextMenu?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: TableRow
  ) => void;
  // expandedRowIds: UseExpandedState<never>['expanded'];
  mode: TableMode;
  classes?: ICssClasses;
  /**
   * @default "2.25rem"
   */
  rowHeightRem?: string;
  visibleColumns: ColumnInstance[];
  customVisibleColumns?: string[];
  manualGroupBy?: boolean;
  isVirtual: boolean;
  fitContentHeight?: boolean;
  threshold?: number;
  minimumBatchSize?: number;
  width: number;
  onScroll?: (params: {
    scrollLeft: number;
    scrollWidth: number;
    clientWidth: number;
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => void;
  /**
   * On mobile devices we show a toolbar at the bottom of the container. Pass
   * the toolbar's height here ot add as much bottom padding to the table
   * container, so the toolbar does not obstruct the last row.
   */
  bottomPadding?: string | null;

  entity?: Entities;
  ignoreLongPress?: boolean;
  groupBy?: string[];

  // HACK Workaround to get access to the virtualizer instance without restructuring the whole table.
  virtualizerRef: React.RefObject<Virtualizer<HTMLDivElement, HTMLDivElement>>;
}

const DEFAULT_ROW_HEIGHT = '2.25rem';

/**
 * It's kinda dangerous to use React memo here:
 * Rows are lazy loaded. Rows can change even when "rows" array stay the same.
 * So, shallow comparison of React.memo doesn't work here.
 * But without optimizations of List performance is bad
 * "state" object in props guarantees that rows will update when their state change.
 *
 * Problem appears only with "selectedRowIds" state. Any other state change triggers re-rendering
 */

export function Loader(props: ILoaderProps) {
  const {
    tableContainerRef,
    rows,
    bottomPadding,
    cardConfig,
    cardsView,
    loadMoreItems,
    virtualizerRef,
  } = props;

  const rowHeight = useRemToPx(props.rowHeightRem || DEFAULT_ROW_HEIGHT);

  const isCardsView = Boolean(cardsView && cardConfig);

  const itemSize = useCallback(
    (index: number) => {
      const row = rows[index];
      const isSectionStart = row?.original.meta?.isSectionStart ?? false;
      return isSectionStart ? rowHeight + SEPARATOR_ROW_HEIGHT : rowHeight;
    },
    [rows, rowHeight]
  );

  const total =
    props.allItemsCount === 'rowsLength'
      ? props.rows.length
      : props.allItemsCount;

  const getItemKey = useCallback(
    (index: number) => {
      const prefix = isCardsView ? 'card' : 'row';
      const id = rows[index]?.id || index;
      return `${prefix}-${id}`;
    },
    [isCardsView, rows]
  );

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: total,
    getScrollElement: () => tableContainerRef.current,
    // For some reason when scrolling down the element is not visible. Add some
    // padding to make sure it's on screen.
    scrollPaddingEnd: 64,
    measureElement(element) {
      const height = element.getBoundingClientRect().height;

      return element.dataset.sectionStart === 'true'
        ? height + SEPARATOR_ROW_HEIGHT
        : height;
    },
    getItemKey: getItemKey,
    estimateSize: itemSize,
    overscan: 10,
  });

  useEffect(() => {
    if (virtualizerRef) {
      virtualizerRef.current = rowVirtualizer;
    }
  }, [virtualizerRef, rowVirtualizer]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    virtualItems.forEach((item) => {
      const index = item.index;
      const row = rows[index];

      const isItemLoaded = !!row && !row.original.skeleton;

      if (!isItemLoaded && loadMoreItems) {
        loadMoreItems(index, -1, rows);
      }
    });
  }, [loadMoreItems, rows, total, virtualItems]);

  if (props.fitContentHeight) {
    return (
      <div
        style={{
          display: 'grid',
          width: isCardsView ? '100%' : props.width,
          minWidth: '100%',
          paddingBottom: bottomPadding || null,
        }}
      >
        {rows.map((row) => {
          return (
            <RenderRow key={row.id} height={rowHeight} row={row} {...props} />
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        position: 'relative',
        height: `calc(${rowVirtualizer.getTotalSize()}px + ${
          bottomPadding ?? '0px'
        })`,
        width: isCardsView ? '100%' : props.width,
        minWidth: '100%',
        paddingBottom: bottomPadding || null,
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const row = rows[virtualRow.index];

        return (
          <RenderRow
            key={virtualRow.key}
            height={rowHeight}
            row={row}
            virtualRow={virtualRow}
            rowVirtualizer={rowVirtualizer}
            {...props}
          />
        );
      })}
    </div>
  );
}
